import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Grid,
  IconButton,
  Modal,
  Typography,
} from '@mui/material';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { useToast } from '../../../Hooks/useToast';
import { useUser } from '../../../Hooks/useUser';
import {
  jobApi,
  useCompleteJobMutation,
  useMarkJobReadyMutation,
} from '../../../Redux/api/jobApi';
import { useAppDispatch } from '../../../Redux/store';
import { KaziButton } from '../../../Shared Components/Kazi/KaziButton';
import { ProfileType } from '../../../Types/General';
import { RateTalentModal } from './RateTalentModal';

interface IJobCompletionModalProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  jobInfo: {
    locator: string;
    name: string;
    title: string;
    userType: ProfileType;
    jobType: string;
    avatar: React.ReactNode;
    client_locator: string;
    talent_locator: string;
  };
}

export const JobCompletionModal = ({
  open,
  setOpen,
  jobInfo,
}: IJobCompletionModalProps) => {
  /* ******************** Hooks ******************** */
  const dispatch = useAppDispatch();
  const { hasPremium } = useUser();
  const { showToast } = useToast();
  const [markJobReady, { isLoading: markJobReadyLoading }] =
    useMarkJobReadyMutation();
  const [completeJob, { isLoading: completeJobLoading }] =
    useCompleteJobMutation();
  /* ******************** Variables ******************** */
  const [openRateTalentModal, setOpenRateTalentModal] = useState(false);
  /* ******************** Functions ******************** */
  const handleClose = () => {
    setOpen(false);
  };
  const getPrompt = () => {
    if (
      jobInfo.jobType !== 'Short-term' &&
      jobInfo.userType === ProfileType.Client
    )
      return 'By completing this job, you have hired this Talent. You will no longer be able to message this Talent.';
    if (jobInfo.userType === ProfileType.Client)
      return `By completing this job, you have finished working with this Talent. ${
        !hasPremium ? 'You will no longer be able to message this Talent. ' : ''
      }This Talent will be paid in full after completing this job.`;
    if (jobInfo.userType === ProfileType.Talent)
      return 'By completing this job, you have finished working with this Client. You will no longer be able to message this Client. The Client will need to confirm that the job is complete.';
  };
  const handleCompletion = () => {
    if (jobInfo.jobType !== 'Short-term') {
    } else if (jobInfo.userType === ProfileType.Client) {
      completeJob({
        locator: jobInfo.locator,
        body: {
          receiver_locator: jobInfo.talent_locator,
        },
      })
        .unwrap()
        .then(res => {
          // if (viewApplication) {
          //     setViewApplication(false);
          // }

          showToast({
            text: 'Job marked as completed successfully',
            variant: 'success',
          });

          handleClose();

          setOpenRateTalentModal(true);
        })
        .catch(err => {
          console.log(err);
          showToast({
            text: 'Unable to complete the job',
            variant: 'error',
          });
        });
    } else if (jobInfo.userType === ProfileType.Talent) {
      markJobReady({
        locator: jobInfo.locator,
        body: {
          receiver_locator: jobInfo.client_locator,
        },
      })
        .unwrap()
        .then(res => {
          // if (viewApplication) {
          //     setViewApplication(false);
          // }
          dispatch(jobApi.util.invalidateTags(['JobsByStatus']));
          handleClose();
          showToast({
            text: 'Job successfully marked ready for review by the client',
            variant: 'success',
          });
        })
        .catch(err => {
          console.log(err);
          showToast({
            text: 'Unable to complete the job',
            variant: 'error',
          });
        });
    }
  };
  /* ******************** Effects ******************** */
  /* ******************** JSX ******************** */
  if (!jobInfo) return null;
  return (
    <>
      <Modal
        open={open}
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Card
          sx={{
            width: '600px',
            borderRadius: '12px',
            maxHeight: '95%',
          }}
        >
          <CardHeader
            sx={{
              paddingBottom: 1,
            }}
            action={
              <IconButton onClick={handleClose}>
                <Close color="primary" />
              </IconButton>
            }
          />
          <CardContent
            sx={{
              padding: '0 24px',
            }}
          >
            <Grid container gap={'20px'} flexDirection={'column'}>
              <Typography>Do you want complete this job?</Typography>
              <Grid flex={1} display={'flex'} flexDirection={'row'} gap={2}>
                <Grid
                  display={'flex'}
                  flexDirection={'column'}
                  justifyContent={'center'}
                >
                  {jobInfo.avatar}
                </Grid>
                <Grid display={'flex'} flexDirection={'column'}>
                  <Typography variant="lora-title-large" fontWeight={'bold'}>
                    {jobInfo.name}
                  </Typography>
                  <Typography variant="body-smaller">
                    {jobInfo.title}
                  </Typography>
                </Grid>
              </Grid>
              <Grid display={'flex'} flexDirection={'column'}>
                <Typography variant="body-medium">{getPrompt()}</Typography>
              </Grid>
            </Grid>
          </CardContent>
          <CardActions
            sx={{
              padding: '24px',
            }}
          >
            <Box display={'flex'} flexDirection={'row'} width={'100%'}>
              {
                // isUploading || talentUpdateResponseLoading ?
                // null :
                <>
                  <Box
                    flex={2}
                    display={'flex'}
                    flexDirection={'row'}
                    justifyContent={'flex-end'}
                    gap={1}
                  >
                    <Button variant="outlined" onClick={handleClose}>
                      Cancel
                    </Button>
                    <KaziButton
                      variant="contained"
                      text="Complete"
                      loading={{
                        isLoading: markJobReadyLoading || completeJobLoading,
                        loadingText: 'Completing...',
                      }}
                      onClick={handleCompletion}
                    />
                  </Box>
                </>
              }
            </Box>
          </CardActions>
        </Card>
      </Modal>

      <RateTalentModal
        open={openRateTalentModal}
        setOpen={setOpenRateTalentModal}
        jobInfo={jobInfo}
      />
    </>
  );
};
